import { Show, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import type { Game } from '../../../game/Game';
import { STORAGE_KEY_BALANCE } from '../../../game/constants';
import { gameStore } from '../../../store/gameStore';
import type { StreamerData } from '../../../mocks/streamers/schema';
import { detectStreamerMode } from '../../utils/detectStreamerMode';
import { useMirrorBet } from '../../hooks/useMirrorBet';
import JoinModal from './JoinModal';
import StreamCircle from './StreamCircle';
import StreamerPanel from './StreamerPanel';
import { showToast } from '../ui/ToastNotification';
import { isRealMode, gameApi } from '../../../api';
import type { StreamerRoomUpdatePayload } from '../../../api';

const lsKey = (prefix: string, id: string) => `memePumpStreamer${prefix}:${id}`;

function track(name: string, payload?: Record<string, unknown>) {
  try { (window as any).logEvent?.(name, payload ?? {}); } catch {} // external analytics
}

function readBalance(): number {
  try {
    const v = Number(localStorage.getItem(STORAGE_KEY_BALANCE));
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}

/** Build a StreamerData-compatible object from WS payload. Missing fields show N/A. */
function wsToStreamerData(p: StreamerRoomUpdatePayload): StreamerData {
  const statusMap: Record<string, StreamerData['status']> = { LIVE: 'LIVE', AWAY: 'AWAY', OFFLINE: 'OFFLINE' };
  return {
    streamerId: p.streamerId,
    displayName: p.displayName,
    avatar: p.avatar || '/assets/avatars/cat.png',
    brandColor: p.brandColor || '#a259ff',
    status: statusMap[p.status] || 'OFFLINE',
    sampleBets: p.sampleBets.length > 0
      ? p.sampleBets.map(b => ({ amount: b.amount, currency: 'USD', autoCashout: undefined }))
      : [{ amount: 0, currency: 'USD', autoCashout: undefined }],
    streamerRecentMessages: [],
    roomStats: {
      playersInRoom: p.playersInRoom,
      avgBet: 0,       // N/A — not in WS payload
      topWinners: [],   // N/A
      userRank: undefined,
    },
    hypeThresholds: [],
    promos: [],
    specialEvents: [],
    slogan: undefined,
  };
}

export default function StreamerMode() {
  const [streamer, setStreamer] = createSignal<StreamerData | null>(null);
  const [panel, setPanel] = createSignal<'minimal' | 'compact' | 'expanded'>('minimal');
  const [showJoin, setShowJoin] = createSignal(false);
  const [joined, setJoined] = createSignal(false);
  const [balance, setBalance] = createSignal(readBalance());

  const [amOn, setAmOn] = createSignal(false);
  const [amRounds, setAmRounds] = createSignal(3);
  const [amLeft, setAmLeft] = createSignal(0);
  const [cheerPulse, setCheerPulse] = createSignal(false);

  const { multiplier } = gameStore;
  const { mirrorBet } = useMirrorBet();

  const bet = createMemo(() => streamer()?.sampleBets[0]);
  const currentWin = createMemo(() => (bet()?.amount ?? 0) * multiplier());
  const amCap = createMemo(() => balance() * 0.2);
  const amLocked = createMemo(() => !bet() || bet()!.amount > amCap());

  createEffect(() => {
    if (amOn() && amLocked()) { setAmOn(false); setAmLeft(0); }
  });

  createEffect(() => {
    if (amOn() && amLeft() === 0) setAmOn(false);
  });

  const sid = () => streamer()?.streamerId;

  const toggle = () => {
    const s = panel();
    if (s === 'expanded') { setPanel('compact'); track('streamer_panel_closed', { streamerId: sid() }); return; }
    setPanel(s === 'minimal' ? 'compact' : 'minimal');
  };

  const close = () => {
    const s = panel();
    if (s === 'expanded') { setPanel('compact'); track('streamer_panel_closed', { streamerId: sid() }); }
    else setPanel('minimal');
  };

  const expand = () => { setPanel('expanded'); track('streamer_panel_opened', { streamerId: sid() }); };

  const join = () => {
    const d = streamer();
    if (!d) return;
    setJoined(true);
    try { localStorage.setItem(lsKey('Joined', d.streamerId), 'true'); } catch {}
    setShowJoin(false);
    setPanel('minimal');
    if (isRealMode) gameApi.joinStreamerRoom({ streamerId: d.streamerId });
    track('streamer_join', { streamerId: d.streamerId });
  };

  const skip = (suppress: boolean) => {
    const d = streamer();
    if (!d) return;
    setShowJoin(false);
    if (suppress) try { localStorage.setItem(lsKey('JoinDismissed', d.streamerId), 'true'); } catch {}
  };

  const mirror = (autoPlace = false) => {
    const b = bet();
    if (!b) return;
    mirrorBet({ amount: b.amount, autoCashout: b.autoCashout, slotId: 1, autoPlace });
    track('mirror_bet', { streamerId: sid(), amount: b.amount });
  };

  const toggleAm = (on: boolean) => {
    if (amLocked()) return;
    setAmOn(on);
    setAmLeft(on ? amRounds() : 0);
    track('auto_mirror_toggle', { streamerId: sid(), enabled: on });
  };

  const changeAmRounds = (v: number) => {
    const n = Math.max(1, Math.min(5, Math.floor(v || 1)));
    setAmRounds(n);
    if (amOn()) setAmLeft(n);
  };

  const cheer = () => {
    track('cheer', { streamerId: sid() });
    showToast('info', 'Cheer sent');
    setCheerPulse(true);
    window.setTimeout(() => setCheerPulse(false), 520);
  };

  const autoMirror = () => {
    if (!amOn() || amLeft() <= 0) return;
    mirror(true);
    setAmLeft((p) => Math.max(0, p - 1));
  };

  onMount(() => {
    if (isRealMode) {
      // ── Real mode: WS-based streamer ──
      const params = new URLSearchParams(window.location.search);
      const streamerId = params.get('streamer');
      if (!streamerId) return;

      // Balance synced from gameStore
      createEffect(() => setBalance(gameStore.balance()));

      // Join room via WS
      gameApi.joinStreamerRoom({ streamerId });

      const unsubs: Array<() => void> = [];

      // Room updates → streamer data
      unsubs.push(
        gameApi.on('streamer:roomUpdate', (payload) => {
          if (payload.streamerId !== streamerId) return;
          setStreamer(wsToStreamerData(payload));
        }),
      );

      // Auto-mirror trigger on BET_TIME
      unsubs.push(
        gameApi.on('round:state', (payload) => {
          if (payload.state === 'BET_TIME') autoMirror();
        }),
      );

      // Join/dismiss persisted state
      const wasJoined = (() => { try { return localStorage.getItem(lsKey('Joined', streamerId)) === 'true'; } catch { return false; } })();
      setJoined(wasJoined);
      const dismissed = (() => { try { return localStorage.getItem(lsKey('JoinDismissed', streamerId)) === 'true'; } catch { return false; } })();
      if (!dismissed && !wasJoined) setShowJoin(true);

      track('streamer_view', { streamerId });

      onCleanup(() => {
        unsubs.forEach((u) => u());
        gameApi.leaveStreamerRoom({ streamerId });
      });
    } else {
      // ── Mock mode ──
      const syncBal = () => setBalance(readBalance());
      window.addEventListener('storage', syncBal);
      onCleanup(() => window.removeEventListener('storage', syncBal));

      const attach = (g: Game) => { g.on('roundStart', autoMirror); };
      const eng = gameStore.engine();
      if (eng) attach(eng);
      else {
        const ready = () => { const r = gameStore.engine(); if (r) attach(r); };
        window.addEventListener('memePump:ready', ready, { once: true });
        onCleanup(() => window.removeEventListener('memePump:ready', ready));
      }

      (async () => {
        const data = await detectStreamerMode();
        if (!data) return;
        setStreamer(data);
        track('streamer_view', { streamerId: data.streamerId });
        const wasJoined = (() => { try { return localStorage.getItem(lsKey('Joined', data.streamerId)) === 'true'; } catch { return false; } })();
        setJoined(wasJoined);
        setPanel('minimal');
        const dismissed = (() => { try { return localStorage.getItem(lsKey('JoinDismissed', data.streamerId)) === 'true'; } catch { return false; } })();
        if (!dismissed && !wasJoined) setShowJoin(true);
      })();
    }
  });

  return (
    <>
      <Show when={streamer()}>
        {(data) => (
          <div class="streamer-mode" data-state={panel()}>
            <StreamCircle streamer={data()} state={panel()} onToggle={toggle} />
            <StreamerPanel
              streamer={data()}
              state={panel()}
              joined={joined()}
              multiplier={multiplier()}
              currentWin={currentWin()}
              mirrorDisabled={!bet()}
              autoMirrorEnabled={amOn()}
              autoMirrorRounds={amRounds()}
              autoMirrorRemaining={amLeft()}
              autoMirrorDisabled={amLocked()}
              onClose={close}
              onExpand={expand}
              onJoin={join}
              onMirror={mirror}
              onAutoMirrorToggle={toggleAm}
              onAutoMirrorRoundsChange={changeAmRounds}
              onCheer={cheer}
              cheerPulse={cheerPulse()}
            />
            <JoinModal open={showJoin()} streamer={data()} onJoin={join} onSkip={skip} />
          </div>
        )}
      </Show>
    </>
  );
}
